import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminBlogPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const r = await api.get('/admin/blog');
            setItems(r.data.items || []);
        } catch { toast.error('Failed to load posts'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const remove = async (slug) => {
        if (!window.confirm(`Delete post "${slug}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/blog/${slug}`);
            toast.success('Post deleted');
            load();
        } catch { toast.error('Delete failed'); }
    };

    return (
        <div data-testid="admin-blog-page" className="space-y-6">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Blog</h1>
                    <p className="text-sm text-muted-foreground mt-1">Manage articles published on the site.</p>
                </div>
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-blog-new-button">
                    <Link to="/admin/blog/new" className="inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> New post</Link>
                </Button>
            </header>

            <div className="dt-glass rounded-2xl overflow-hidden">
                <Table data-testid="admin-blog-table">
                    <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Title</TableHead>
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground hidden md:table-cell">Tags</TableHead>
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground hidden lg:table-cell">Slug</TableHead>
                            <TableHead className="text-xs font-mono uppercase tracking-widest text-muted-foreground hidden sm:table-cell">Published</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No posts yet.</TableCell></TableRow>
                        ) : items.map((p) => (
                            <TableRow key={p.slug} className="border-white/10 hover:bg-white/5">
                                <TableCell className="font-medium">{p.title}</TableCell>
                                <TableCell className="hidden md:table-cell">
                                    <div className="flex flex-wrap gap-1">
                                        {(p.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="outline" className="font-mono text-[10px] tracking-widest border-primary/30 text-primary bg-primary/5">{t}</Badge>)}
                                    </div>
                                </TableCell>
                                <TableCell className="hidden lg:table-cell font-mono text-xs text-muted-foreground">{p.slug}</TableCell>
                                <TableCell className="hidden sm:table-cell font-mono text-xs text-muted-foreground">{p.published_at ? new Date(p.published_at).toLocaleDateString() : '—'}</TableCell>
                                <TableCell className="text-right">
                                    <div className="inline-flex items-center gap-1">
                                        <Button asChild variant="ghost" size="icon" title="View"><Link to={`/blog/${p.slug}`} target="_blank"><ExternalLink className="h-4 w-4" /></Link></Button>
                                        <Button asChild variant="ghost" size="icon" title="Edit" data-testid={`admin-blog-edit-${p.slug}`}><Link to={`/admin/blog/${p.slug}`}><Pencil className="h-4 w-4" /></Link></Button>
                                        <Button variant="ghost" size="icon" title="Delete" data-testid={`admin-blog-delete-${p.slug}`} onClick={() => remove(p.slug)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
